import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { getBackendConfigInstance } from '../config/backendConfig';
import { trackEvent, logError } from '../utils/observability';
import { authFetch } from '../utils/authFetch';
import { logMembershipDiagnostics } from '../utils/membershipDiagnostics';

const APP_ENV = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) || 'prod';
const isStaging = APP_ENV === 'staging' || (typeof window !== 'undefined' && (window.location.hostname || '').includes('staging'));

const MEMBERSHIP_TTL_MS = 60 * 1000; // 60s in-memory TTL (soft cache; forced refresh bypasses)
const MEMBERSHIP_DEDUPE_MS = 2000;   // 2s dedupe window
const MEMBERSHIP_BACKOFF_BASE_MS = 8000;
const MEMBERSHIP_BACKOFF_MAX_MS = 60000;
const DEVICE_SESSION_LIMIT = 2;
const DEVICE_SESSION_COLLECTION = 'userDeviceSessions';
const DEVICE_ID_KEY = 'artmosphere_device_id_v1';
const DEVICE_LIMIT_ERROR_KEY = 'auth_device_limit_error';
const SESSION_STALE_MS = 24 * 60 * 60 * 1000;
const SESSION_HEARTBEAT_MS = 60 * 1000;

/** Periodic server revalidation while the app stays open (typical for subscription products). */
const MEMBERSHIP_POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Minimum gap between forced refreshes (visibility + poll share one throttle). */
const MEMBERSHIP_FORCED_REFRESH_MIN_GAP_MS = 60 * 1000;

/**
 * Single source of truth for normalized user. Guarantees uid, email, accountType, mentorType, isPrimaryId.
 * accountType defaults to 'student' when missing so route guards never see undefined.
 */
function normalizeUserProfile(rawAuthUser, firestoreDoc = null) {
  if (!rawAuthUser) return null;
  const base = {
    uid: rawAuthUser.uid,
    email: rawAuthUser.email ?? null,
    displayName: rawAuthUser.displayName ?? null,
    photoURL: rawAuthUser.photoURL ?? null,
    accountType: 'student',
    mentorType: null,
    isPrimaryId: null,
    mentorName: null,
  };
  if (firestoreDoc && typeof firestoreDoc === 'object') {
    const at = firestoreDoc.accountType;
    base.accountType = at == null || at === '' ? 'student' : String(at);
    base.mentorType = firestoreDoc.mentorType ?? null;
    base.isPrimaryId = firestoreDoc.isPrimaryId ?? null;
    base.mentorName = firestoreDoc.mentorName ?? null;
  }
  return base;
}

function withTimeout(promise, ms, fallback = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

function getOrCreateDeviceId() {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const generated =
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  } catch (_) {
    return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getSessionDocId(uid, deviceId) {
  return `${uid}_${deviceId}`;
}

function toMillis(tsLike) {
  try {
    if (!tsLike) return 0;
    if (typeof tsLike === 'number') return tsLike;
    if (typeof tsLike?.toMillis === 'function') return tsLike.toMillis();
    if (typeof tsLike?.toDate === 'function') return tsLike.toDate().getTime();
    if (tsLike?._seconds) return tsLike._seconds * 1000 + (tsLike._nanoseconds || 0) / 1000000;
    const d = new Date(tsLike);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch (_) {
    return 0;
  }
}

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  login: async () => ({ user: null }),
  logout: async () => {},
  loading: true,
  authResolved: false,
  membership: null,
  membershipLoading: false,
  membershipError: null,
  refreshMembership: async () => null,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState(null);

  const membershipMemoryRef = useRef({ data: null, ts: 0 });
  const membershipFetchInFlightRef = useRef(null);
  const membershipBackoffRef = useRef({ until: 0, failures: 0, reason: null });
  const deviceIdRef = useRef(getOrCreateDeviceId());

  const touchCurrentDeviceSession = useCallback(async (uid, email = null) => {
    if (!uid) return;
    const deviceId = deviceIdRef.current || getOrCreateDeviceId();
    const docRef = doc(db, DEVICE_SESSION_COLLECTION, getSessionDocId(uid, deviceId));
    await setDoc(
      docRef,
      {
        uid,
        email: email || null,
        deviceId,
        active: true,
        lastActiveAt: serverTimestamp(),
        lastActiveClientAt: Date.now(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        platform: typeof navigator !== 'undefined' ? navigator.platform : '',
      },
      { merge: true }
    );
  }, []);

  const markCurrentDeviceSessionInactive = useCallback(async (uid) => {
    if (!uid) return;
    const deviceId = deviceIdRef.current || getOrCreateDeviceId();
    const docRef = doc(db, DEVICE_SESSION_COLLECTION, getSessionDocId(uid, deviceId));
    await setDoc(
      docRef,
      {
        uid,
        deviceId,
        active: false,
        endedAt: serverTimestamp(),
        endedAtClientAt: Date.now(),
      },
      { merge: true }
    );
  }, []);

  const enforceDeviceSessionLimit = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.uid) return { allowed: false, reason: 'no_user' };
    const uid = firebaseUser.uid;
    const email = firebaseUser.email || null;
    const deviceId = deviceIdRef.current || getOrCreateDeviceId();
    const now = Date.now();

    // Local dev: two-device cap + stale session docs cause confusing sign-outs; still touch session for debugging.
    if (typeof window !== 'undefined') {
      const h = window.location.hostname || '';
      if (h === 'localhost' || h === '127.0.0.1') {
        try {
          await touchCurrentDeviceSession(uid, email);
        } catch (_) {}
        return { allowed: true, reason: 'local_dev' };
      }
    }

    try {
      const q = query(
        collection(db, DEVICE_SESSION_COLLECTION),
        where('uid', '==', uid),
        where('active', '==', true)
      );
      const snap = await getDocs(q);
      const activeSessions = [];
      const staleWrites = [];

      snap.docs.forEach((d) => {
        const data = d.data() || {};
        const lastSeen = data.lastActiveClientAt || toMillis(data.lastActiveAt);
        const isStale = !lastSeen || (now - lastSeen > SESSION_STALE_MS);
        if (isStale) {
          staleWrites.push(
            setDoc(
              d.ref,
              {
                active: false,
                endedAt: serverTimestamp(),
                endedAtClientAt: now,
                endedReason: 'stale',
              },
              { merge: true }
            )
          );
          return;
        }
        activeSessions.push({ id: d.id, ...data });
      });

      if (staleWrites.length) {
        await Promise.allSettled(staleWrites);
      }

      const hasCurrentDevice = activeSessions.some((s) => s.deviceId === deviceId);

      // Room for this device: evict oldest active sessions instead of hard-blocking login (prod users
      // often hit stale rows from old browsers; blocking looked like "cannot log in").
      if (!hasCurrentDevice && activeSessions.length >= DEVICE_SESSION_LIMIT) {
        const evictCount = activeSessions.length - DEVICE_SESSION_LIMIT + 1;
        const sorted = [...activeSessions].sort((a, b) => {
          const ta = a.lastActiveClientAt || toMillis(a.lastActiveAt);
          const tb = b.lastActiveClientAt || toMillis(b.lastActiveAt);
          return ta - tb;
        });
        const victims = sorted.slice(0, Math.max(0, evictCount));
        await Promise.allSettled(
          victims.map((s) =>
            setDoc(
              doc(db, DEVICE_SESSION_COLLECTION, s.id),
              {
                active: false,
                endedAt: serverTimestamp(),
                endedAtClientAt: now,
                endedReason: 'evicted_for_new_device',
              },
              { merge: true }
            )
          )
        );
      }

      await touchCurrentDeviceSession(uid, email);
      return { allowed: true };
    } catch (error) {
      const code = String(error?.code || '');
      // Rules not deployed (or restricted env) should not block login in development.
      if (code === 'permission-denied' || /permission/i.test(code)) {
        if (isStaging) {
          console.warn('[auth] device session limit skipped: permission denied');
        }
        return { allowed: true, reason: 'permission_denied' };
      }
      throw error;
    }
  }, [touchCurrentDeviceSession]);

  // Stable identity so ProtectedRoute and other consumers don't re-run effects every render.
  const fetchMembershipStatus = useCallback(async (uid, { force = false } = {}) => {
    if (!uid) return null;

    const now = Date.now();
    const mem = membershipMemoryRef.current;
    const backoff = membershipBackoffRef.current;

    // Circuit-breaker: avoid hammering /membership/status during repeated failures.
    // Allow explicit force refresh to bypass this.
    if (!force && backoff.until > now) {
      if (mem.data && (now - mem.ts) < MEMBERSHIP_TTL_MS) {
        setMembership(mem.data);
        return mem.data;
      }
      return null;
    }
    if (!force && mem.data && (now - mem.ts) < MEMBERSHIP_TTL_MS) {
      if (isStaging) console.log('[auth] membership cached', { age: Math.round((now - mem.ts) / 1000) + 's' });
      setMembership(mem.data);
      return mem.data;
    }

    // Forced refresh must not block on an older in-flight fetch (can hang or return stale data after mutations).
    if (membershipFetchInFlightRef.current && !force) {
      try {
        const existing = await membershipFetchInFlightRef.current;
        return existing;
      } catch (_) {
        if (mem.data && (now - mem.ts) < MEMBERSHIP_TTL_MS) {
          if (isStaging) console.log('[auth] membership fetch failed, using recent cache');
          setMembership(mem.data);
          return mem.data;
        }
        return null;
      }
    }

    const baseUrl = getBackendConfigInstance().baseUrl;
    const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
    const url = `${apiBase}/membership/status?uid=${uid}&_t=${now}`;

    const doFetch = async (timeoutMs = 28000) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await authFetch(url, { signal: controller.signal, cache: 'no-store' });
        clearTimeout(timeoutId);
        return res;
      } catch (e) {
        clearTimeout(timeoutId);
        throw e;
      }
    };

    const promise = (async () => {
      try {
        setMembershipLoading(true);
        setMembershipError(null);
        const res = await doFetch();

        if (!res.ok) {
          if (isStaging) logMembershipDiagnostics({ route: 'membership/status', status: res.status, statusText: res.statusText, failureType: res.status === 401 ? '401' : res.status === 403 ? '403' : 'inactive' });
          // Any non-ok response should back off to prevent retry storms (401/403/5xx).
          const nextFailures = (membershipBackoffRef.current.failures || 0) + 1;
          const waitMs = Math.min(MEMBERSHIP_BACKOFF_BASE_MS * Math.pow(2, nextFailures - 1), MEMBERSHIP_BACKOFF_MAX_MS);
          const reason =
            res.status === 401 ? 'unauthorized' :
            res.status === 403 ? 'forbidden' :
            res.status >= 500 ? 'server' : 'non_ok';
          membershipBackoffRef.current = {
            until: Date.now() + waitMs,
            failures: nextFailures,
            reason,
          };
          if (mem.data && (Date.now() - mem.ts) < MEMBERSHIP_TTL_MS) {
            if (isStaging) console.log('[auth] membership non-ok, using recent cache');
            setMembership(mem.data);
            return mem.data;
          }
          return null;
        }

        const data = await res.json();
        membershipMemoryRef.current = { data, ts: Date.now() };
        membershipBackoffRef.current = { until: 0, failures: 0, reason: null };
        setMembership(data);
        setMembershipError(null);
        if (isStaging) console.log('[auth] membership success', { allowed: !!data?.allowed, status: data?.membership?.status });
        return data;
      } catch (error) {
        setMembershipError(error?.message || String(error));
        const errMsg = String(error?.message || error);
        const isAbort = error?.name === 'AbortError' || /aborted/i.test(errMsg);
        const isNetwork = /load failed|failed to fetch|networkerror|aborterror/i.test(errMsg);
        if (isStaging) logMembershipDiagnostics({ route: 'membership/status', failureType: isAbort ? 'timeout' : isNetwork ? 'network' : 'cors' });
        // Network/timeouts: back off progressively to stop continuous flashing/retries.
        if (isNetwork || isAbort) {
          const nextFailures = (membershipBackoffRef.current.failures || 0) + 1;
          const waitMs = Math.min(MEMBERSHIP_BACKOFF_BASE_MS * Math.pow(2, nextFailures - 1), MEMBERSHIP_BACKOFF_MAX_MS);
          membershipBackoffRef.current = {
            until: Date.now() + waitMs,
            failures: nextFailures,
            reason: isAbort ? 'timeout' : 'network',
          };
        }
        if (mem.data && (Date.now() - mem.ts) < MEMBERSHIP_TTL_MS) {
          if (isStaging) console.log('[auth] membership transient failure, using recent cache');
          setMembership(mem.data);
          return mem.data;
        }
        // One quick retry only — long multi-second retries made local dev feel "stuck" when the API was down.
        if (isNetwork || isAbort) {
          try {
            await new Promise((r) => setTimeout(r, 400));
            const res2 = await doFetch(12000);
            if (res2.ok) {
              const data = await res2.json();
              membershipMemoryRef.current = { data, ts: Date.now() };
              membershipBackoffRef.current = { until: 0, failures: 0, reason: null };
              setMembership(data);
              return data;
            }
          } catch (_) {}
        }
        // Avoid noisy error logging during expected request aborts/timeouts.
        if (!isAbort && !isNetwork) {
          logError(error, { where: 'AuthContext.fetchMembershipStatus' });
        }
        return null;
      } finally {
        setMembershipLoading(false);
        // Avoid clearing a newer in-flight fetch when force:true starts a second request before the first finishes.
        if (membershipFetchInFlightRef.current === promise) {
          membershipFetchInFlightRef.current = null;
        }
      }
    })();

    membershipFetchInFlightRef.current = promise;
    return promise;
  }, []);

  const fetchUserAccountType = async (firebaseUser, retryCount = 0) => {
    try {
      const userDocPromise = getDoc(doc(db, 'users', firebaseUser.uid));
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore read timeout')), 5000));
      const userDoc = await Promise.race([userDocPromise, timeoutPromise]);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (isStaging) console.log('[auth] Firestore user doc', { accountType: userData.accountType == null || userData.accountType === '' ? 'student' : userData.accountType });
        return normalizeUserProfile(firebaseUser, userData);
      }

      if (retryCount < 2) {
        await new Promise((r) => setTimeout(r, 1000));
        return fetchUserAccountType(firebaseUser, retryCount + 1);
      }
      return null;
    } catch (error) {
      if (error.message === 'Firestore read timeout' || error.code === 'permission-denied') return null;
      return null;
    }
  };

  const fetchUserByAccountType = async (email, accountType, mentorType = null) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const baseUrl = getBackendConfigInstance().baseUrl;
      const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
      const response = await authFetch(
        `${apiBase}/membership/get-user-by-account-type?email=${encodeURIComponent(email)}&accountType=${accountType}${mentorType ? `&mentorType=${mentorType}` : ''}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      const data = await response.json();
      return data.ok ? data.user : null;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (cancelled) return;
      if (isStaging) console.log('[auth] state changed', firebaseUser ? { uid: firebaseUser.uid } : 'signed out');

      if (!firebaseUser) {
        setUser(null);
        setMembership(null);
        membershipMemoryRef.current = { data: null, ts: 0 };
        setAuthResolved(true);
        setLoading(false);
        return;
      }

      // Never let auth bootstrap block the entire app indefinitely.
      const authBootstrapFailsafe = setTimeout(() => {
        if (cancelled) return;
        const normalizedUser = normalizeUserProfile(firebaseUser, null);
        setUser(normalizedUser);
        setAuthResolved(true);
        setLoading(false);
      }, 15000);

      try {
        const sessionCheck = await withTimeout(
          enforceDeviceSessionLimit(firebaseUser),
          5000,
          { allowed: true, reason: 'timeout' }
        );
        if (!sessionCheck?.allowed) {
          const msg = 'Login limit reached: this account is already active on 2 devices. Please log out from another device and try again.';
          try {
            localStorage.setItem(DEVICE_LIMIT_ERROR_KEY, msg);
          } catch (_) {}
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('auth-device-limit', { detail: { message: msg } }));
            }
          } catch (_) {}
          await auth.signOut();
          setUser(null);
          setAuthResolved(true);
          setLoading(false);
          clearTimeout(authBootstrapFailsafe);
          return;
        }
      } catch (sessionErr) {
        if (sessionErr?.code !== 'permission-denied') {
          logError(sessionErr, { where: 'AuthContext.enforceDeviceSessionLimit' });
        }
      }

      const userWithAccountType = await withTimeout(
        fetchUserAccountType(firebaseUser),
        7000,
        null
      );
      if (cancelled) return;

      if (!userWithAccountType) {
        const normalizedUser = normalizeUserProfile(firebaseUser, null);
        setUser(normalizedUser);
        if (isStaging) console.log('[auth] user normalized', { accountType: normalizedUser.accountType });
        fetchMembershipStatus(normalizedUser.uid).catch(() => {});
        setAuthResolved(true);
        setLoading(false);
        clearTimeout(authBootstrapFailsafe);
        return;
      }

      if (userWithAccountType.accountType === 'student') {
        const accountPreference = localStorage.getItem(`account_preference_${firebaseUser.email}`);
        if (accountPreference === 'student') {
          setUser(userWithAccountType);
          fetchMembershipStatus(userWithAccountType.uid).catch(() => {});
          setAuthResolved(true);
          setLoading(false);
          clearTimeout(authBootstrapFailsafe);
          return;
        }
        if (accountPreference === 'organization' || accountPreference === 'mentor') {
          const mentorType = accountPreference === 'organization' ? 'organization' : 'single';
          const mentorUser = await Promise.race([
            fetchUserByAccountType(firebaseUser.email, 'mentor', mentorType),
            new Promise((r) => setTimeout(() => r(null), 3000)),
          ]);
          if (cancelled) return;
          if (mentorUser) {
            setUser(normalizeUserProfile(firebaseUser, mentorUser));
            setAuthResolved(true);
            setLoading(false);
            clearTimeout(authBootstrapFailsafe);
            return;
          }
        }
        const lastLoginTime = parseInt(localStorage.getItem(`last_login_${firebaseUser.uid}`) || '0', 10);
        if (Date.now() - lastLoginTime < 5000) {
          const orgUser = await Promise.race([
            fetchUserByAccountType(firebaseUser.email, 'mentor', 'organization'),
            new Promise((r) => setTimeout(() => r(null), 3000)),
          ]);
          if (cancelled) return;
          if (orgUser) {
            setUser(normalizeUserProfile(firebaseUser, orgUser));
            setAuthResolved(true);
            setLoading(false);
            clearTimeout(authBootstrapFailsafe);
            return;
          }
          const mentorUser = await Promise.race([
            fetchUserByAccountType(firebaseUser.email, 'mentor', 'single'),
            new Promise((r) => setTimeout(() => r(null), 3000)),
          ]);
          if (cancelled) return;
          if (mentorUser) {
            setUser(normalizeUserProfile(firebaseUser, mentorUser));
            setAuthResolved(true);
            setLoading(false);
            clearTimeout(authBootstrapFailsafe);
            return;
          }
        }
      }

      setUser(userWithAccountType);
      if (isStaging) console.log('[auth] user normalized', { accountType: userWithAccountType.accountType });
      fetchMembershipStatus(userWithAccountType.uid).catch(() => {});
      setAuthResolved(true);
      setLoading(false);
      clearTimeout(authBootstrapFailsafe);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    import('../utils/dashboardJourneyHero')
      .then(({ prefetchJourneyHeroSlides }) => {
        if (!cancelled) prefetchJourneyHeroSlides(user.uid);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const intervalId = setInterval(() => {
      touchCurrentDeviceSession(user.uid, user.email).catch(() => {});
    }, SESSION_HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        touchCurrentDeviceSession(user.uid, user.email).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.uid, user?.email, touchCurrentDeviceSession]);

  // Industry-standard entitlement refresh without logging out: revalidate when the user returns to the tab
  // and on an interval while the document is visible, so mid-session plan expiry is picked up from the server.
  useEffect(() => {
    if (!user?.uid) return undefined;

    const uid = user.uid;
    let visibilityDebounce = null;
    let lastForcedAt = 0;

    const runForcedRefresh = () => {
      const now = Date.now();
      if (now - lastForcedAt < MEMBERSHIP_FORCED_REFRESH_MIN_GAP_MS) return;
      lastForcedAt = now;
      fetchMembershipStatus(uid, { force: true }).catch(() => {});
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (visibilityDebounce) clearTimeout(visibilityDebounce);
      visibilityDebounce = setTimeout(() => {
        visibilityDebounce = null;
        runForcedRefresh();
      }, 500);
    };

    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        runForcedRefresh();
      }
    }, MEMBERSHIP_POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (visibilityDebounce) clearTimeout(visibilityDebounce);
      clearInterval(pollId);
    };
  }, [user?.uid, fetchMembershipStatus]);

  const login = async (firebaseUser) => {
    if (firebaseUser?.uid) {
      localStorage.setItem(`last_login_${firebaseUser.uid}`, Date.now().toString());
      trackEvent('login_success', { userId: firebaseUser.uid, email: firebaseUser.email || null, source: 'auth_context' });
    }
    return { user: firebaseUser };
  };

  const logout = async () => {
    try {
      if (user?.uid) {
        await markCurrentDeviceSessionInactive(user.uid).catch(() => {});
        localStorage.removeItem(`last_login_${user.uid}`);
        if (user.email) localStorage.removeItem(`account_preference_${user.email}`);
      }
      await auth.signOut();
      trackEvent('logout', { userId: user?.uid || null });
    } catch (error) {
      logError(error, { where: 'AuthContext.logout' });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        loading,
        authResolved,
        membership,
        membershipLoading,
        membershipError,
        refreshMembership: fetchMembershipStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  try {
    const context = useContext(AuthContext);
    if (context === undefined || context === null) {
      return {
        user: null,
        isAuthenticated: false,
        login: async () => ({ user: null }),
        logout: async () => {},
        loading: true,
        authResolved: false,
        membership: null,
        membershipLoading: false,
        membershipError: null,
        refreshMembership: async () => null,
      };
    }
    return context;
  } catch (error) {
    return {
      user: null,
      isAuthenticated: false,
      login: async () => ({ user: null }),
      logout: async () => {},
      loading: true,
      authResolved: false,
      membership: null,
      membershipLoading: false,
      membershipError: null,
      refreshMembership: async () => null,
    };
  }
}
